/**
 * BackfillTimeline — the draggable day strip inside the backfill wizard.
 *
 * Layout is **percentage-based**, computed straight from the time ratios, so it
 * renders correctly at any width including zero. That matters more than it
 * looks: the `setupTests.ts` ResizeObserver stub never fires its callback, so a
 * width-measured timeline would render nothing at all in every existing test
 * (the same trap `HabitStats.test.tsx` has to work around for recharts). No
 * ResizeObserver here, and nothing to mock for a component to appear.
 *
 * Pixel measurement is needed only to turn a pointer position into a time, and
 * only while a drag is in flight — so the track's rect is read once, at
 * pointerdown, and reused for the gesture.
 *
 * No drag library: the project has none (`wx-react-gantt` is declared but never
 * imported), and raw pointer events are a few dozen lines.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';

import {
  Edge,
  EditMode,
  Interval,
  Segment,
  durationMs,
  snapTime,
} from '../utils/backfillTimeline';

export type DragPhase = 'start' | 'move' | 'end';

export interface BackfillTimelineProps {
  segments: Segment[];
  /** Already-saved entries. Locked here — they are edited on the page, not in the wizard. */
  tracked: Interval[];
  viewStart: Date;
  viewEnd: Date;
  selectedId: string | null;
  /** The walk cursor, highlighted distinctly from a manual selection. */
  currentId: string | null;
  editMode: EditMode;
  snapMinutes: number;
  /** Magnet times an edge snaps to in preference to the grid. */
  magnets?: Date[];
  onSelect: (id: string) => void;
  onEdgeDrag: (id: string, edge: Edge, time: Date, phase: DragPhase) => void;
}

const fmtTime = (d: Date): string =>
  d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const fmtMinutes = (totalMinutes: number): string => {
  const whole = Math.max(0, Math.round(totalMinutes));
  const h = Math.floor(whole / 60);
  const m = whole % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

/** Whole-hour ticks inside the view, for the ruler. */
const hourTicks = (start: Date, end: Date): Date[] => {
  const ticks: Date[] = [];
  const cursor = new Date(start);
  cursor.setMinutes(0, 0, 0);
  if (cursor < start) cursor.setHours(cursor.getHours() + 1);
  while (cursor <= end && ticks.length < 48) {
    ticks.push(new Date(cursor));
    cursor.setHours(cursor.getHours() + 1);
  }
  return ticks;
};

const BackfillTimeline: React.FC<BackfillTimelineProps> = ({
  segments,
  tracked,
  viewStart,
  viewEnd,
  selectedId,
  currentId,
  editMode,
  snapMinutes,
  magnets = [],
  onSelect,
  onEdgeDrag,
}) => {
  const theme = useTheme();
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ id: string; edge: Edge; left: number; width: number } | null>(null);
  const [dragging, setDragging] = useState<{ id: string; edge: Edge } | null>(null);

  const spanMs = viewEnd.getTime() - viewStart.getTime();

  /** Position as a percentage of the view — no layout measurement required. */
  const pct = useCallback(
    (time: Date): number => {
      if (spanMs <= 0) return 0;
      const ratio = (time.getTime() - viewStart.getTime()) / spanMs;
      return Math.min(100, Math.max(0, ratio * 100));
    },
    [spanMs, viewStart],
  );

  const ticks = useMemo(() => hourTicks(viewStart, viewEnd), [viewStart, viewEnd]);

  const timeFromClientX = useCallback(
    (clientX: number): Date | null => {
      const drag = dragRef.current;
      if (!drag || drag.width <= 0 || spanMs <= 0) return null;
      const ratio = (clientX - drag.left) / drag.width;
      const raw = new Date(viewStart.getTime() + ratio * spanMs);
      return snapTime(raw, snapMinutes, magnets);
    },
    [magnets, snapMinutes, spanMs, viewStart],
  );

  const beginDrag = useCallback(
    (event: React.PointerEvent<HTMLElement>, id: string, edge: Edge) => {
      const track = trackRef.current;
      if (!track) return;
      // Read the rect once per gesture: the track cannot resize mid-drag, and
      // this keeps the move handler allocation-free.
      const rect = track.getBoundingClientRect();
      dragRef.current = { id, edge, left: rect.left, width: rect.width };
      setDragging({ id, edge });
      // Optional-chained: jsdom does not implement pointer capture at all.
      event.currentTarget.setPointerCapture?.(event.pointerId);
      event.stopPropagation();
      event.preventDefault();
      onEdgeDrag(id, edge, new Date(), 'start');
    },
    [onEdgeDrag],
  );

  const moveDrag = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      const time = timeFromClientX(event.clientX);
      if (time) onEdgeDrag(drag.id, drag.edge, time, 'move');
    },
    [onEdgeDrag, timeFromClientX],
  );

  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      dragRef.current = null;
      setDragging(null);
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      onEdgeDrag(drag.id, drag.edge, new Date(), 'end');
    },
    [onEdgeDrag],
  );

  /**
   * Arrow keys nudge the focused edge. Handled locally and stopped from
   * bubbling so the wizard's window-level walk shortcuts never see them.
   */
  const handleHandleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>, segment: Segment, edge: Edge) => {
      const claimed = ['ArrowLeft', 'ArrowRight'];
      if (!claimed.includes(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      // Shift takes the fine step; the grid step is the default.
      const stepMinutes = event.shiftKey ? 1 : snapMinutes;
      const direction = event.key === 'ArrowLeft' ? -1 : 1;
      const base = edge === 'start' ? segment.start : segment.end;
      const next = new Date(base.getTime() + direction * stepMinutes * 60000);
      onEdgeDrag(segment.id, edge, next, 'start');
      onEdgeDrag(segment.id, edge, next, 'move');
      onEdgeDrag(segment.id, edge, next, 'end');
    },
    [onEdgeDrag, snapMinutes],
  );

  const stateColour = (segment: Segment): string => {
    if (segment.state === 'skipped') return theme.palette.action.disabledBackground;
    if (segment.state === 'filled') return theme.palette.primary.main;
    return theme.palette.action.selected;
  };

  const activeSegment = segments.find((segment) => segment.id === dragging?.id) || null;

  return (
    <Box data-backfill-timeline="" sx={{ mb: 1 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          mb: 0.5,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {fmtTime(viewStart)} – {fmtTime(viewEnd)}
        </Typography>
        <Typography variant="caption" color="text.secondary" data-testid="timeline-mode">
          {editMode === 'ripple' ? 'Ripple: move neighbour' : 'Roll: shorten neighbour'}
        </Typography>
      </Box>

      {/* Ruler */}
      <Box sx={{ position: 'relative', height: 16 }}>
        {ticks.map((tick) => (
          <Typography
            key={tick.toISOString()}
            variant="caption"
            color="text.disabled"
            sx={{
              position: 'absolute',
              left: `${pct(tick)}%`,
              transform: 'translateX(-50%)',
              fontSize: 10,
              whiteSpace: 'nowrap',
            }}
          >
            {fmtTime(tick)}
          </Typography>
        ))}
      </Box>

      {/* Track */}
      <Box
        ref={trackRef}
        data-timeline-track=""
        data-testid="timeline-track"
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        sx={{
          position: 'relative',
          height: 54,
          borderRadius: 1,
          bgcolor: 'action.hover',
          overflow: 'hidden',
          touchAction: 'none',
        }}
      >
        {/* Already-tracked entries: locked, and soft walls for a drag. */}
        {tracked.map((entry, index) => (
          <Tooltip
            key={`tracked-${index}`}
            title={`Already tracked · ${fmtTime(entry.start)} – ${fmtTime(entry.end)}`}
          >
            <Box
              data-timeline-tracked=""
              data-testid="timeline-tracked"
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${pct(entry.start)}%`,
                width: `${Math.max(0, pct(entry.end) - pct(entry.start))}%`,
                bgcolor: 'action.disabledBackground',
                backgroundImage:
                  'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(128,128,128,0.25) 4px, rgba(128,128,128,0.25) 8px)',
              }}
            />
          </Tooltip>
        ))}

        {/* Proposed segments */}
        {segments.map((segment) => {
          const isSelected = segment.id === selectedId;
          const isCurrent = segment.id === currentId;
          const left = pct(segment.start);
          const width = Math.max(0, pct(segment.end) - left);
          const label = `${fmtTime(segment.start)} – ${fmtTime(segment.end)}`;
          return (
            <Box
              key={segment.id}
              sx={{
                position: 'absolute',
                top: 6,
                bottom: 6,
                left: `${left}%`,
                width: `${width}%`,
              }}
            >
              <Box
                component="button"
                type="button"
                data-timeline-segment={segment.id}
                data-testid="timeline-segment"
                aria-label={`${label}${segment.state === 'skipped' ? ' (skipped)' : ''}`}
                aria-current={isCurrent ? 'step' : undefined}
                aria-pressed={isSelected}
                onClick={() => onSelect(segment.id)}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  border: isCurrent
                    ? `2px solid ${theme.palette.secondary.main}`
                    : `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  cursor: 'pointer',
                  padding: 0,
                  bgcolor: stateColour(segment),
                  opacity: segment.state === 'skipped' ? 0.5 : 1,
                  outline: isSelected ? `2px solid ${theme.palette.info.main}` : 'none',
                  outlineOffset: 1,
                  // A ripple moved this rather than the owner placing it: the
                  // duration is confirmed, the placement is an inference.
                  backgroundImage: segment.movedByRipple
                    ? 'repeating-linear-gradient(-45deg, transparent, transparent 5px, rgba(255,255,255,0.35) 5px, rgba(255,255,255,0.35) 10px)'
                    : 'none',
                }}
              />
              {isSelected &&
                (['start', 'end'] as Edge[]).map((edge) => (
                  <Box
                    key={edge}
                    component="button"
                    type="button"
                    data-timeline-handle={`${segment.id}:${edge}`}
                    data-testid={`timeline-handle-${edge}`}
                    aria-label={`Adjust ${edge === 'start' ? 'start' : 'end'} of ${label}`}
                    onPointerDown={(event: React.PointerEvent<HTMLElement>) =>
                      beginDrag(event, segment.id, edge)
                    }
                    onKeyDown={(event: React.KeyboardEvent<HTMLElement>) =>
                      handleHandleKeyDown(event, segment, edge)
                    }
                    sx={{
                      position: 'absolute',
                      top: -2,
                      bottom: -2,
                      [edge === 'start' ? 'left' : 'right']: -4,
                      width: 10,
                      border: 'none',
                      borderRadius: 1,
                      padding: 0,
                      cursor: 'ew-resize',
                      bgcolor: theme.palette.info.main,
                      touchAction: 'none',
                    }}
                  />
                ))}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ minHeight: 20, mt: 0.5 }}>
        {activeSegment ? (
          <Typography variant="caption" color="text.secondary" data-testid="timeline-readout">
            {fmtTime(activeSegment.start)} – {fmtTime(activeSegment.end)} ·{' '}
            {fmtMinutes(durationMs(activeSegment) / 60000)}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.disabled">
            Click a block to select it, then drag its edges. Hatched blocks were moved
            by a ripple.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default BackfillTimeline;
